/**
 * OperationProgress — one durable batch operation's progress, grouped by why
 * each item did not go out.
 *
 * The bar counts ITEMS, not percent: `aria-valuenow` is applied+skipped+failed
 * out of `total`, so a screen reader hears "3 of 47" rather than "6%". That is
 * why this doesn't reuse `<Progress/>`, whose Radix root is percent-only.
 *
 * Skips are grouped from `outcomes`; when the BE degrades and sends a count
 * without per-item outcomes, the count is still shown rather than silently
 * dropped.
 */
import { useEffect, useRef } from 'react';
import { useOutreachOperation } from '@discovr/features';
import type { OutreachOperation } from '@discovr/types';
import { Skeleton } from '../../../components/ui/skeleton';
import { failedTotal } from './operationOutcomes';
import { skipLabel } from './outreachSendCopy';

export interface OperationProgressProps {
  campaignId: string;
  operationId: string;
  /** Fired once when the operation leaves `running`. */
  onDone?: (operation: OutreachOperation) => void;
  /** Fired once if the progress poll itself fails (a network/read error on
   *  the poll route, not the batch failing) — lets the caller release
   *  whatever "busy" state it was holding for this operation, rather than
   *  staying disabled forever with no way to retry (there's no `operation`
   *  to pass `onDone`, and `onDone` never fires without one). */
  onError?: () => void;
  /** Copy for a skip/fail reason in the outcome list — defaults to the
   *  first-touch batch's own `skipLabel`. A caller reusing this bar for a
   *  different operation family (Task 26's `approve_drafts`, whose skip
   *  reasons read differently — "replied" ⇒ "Replied before approval" here,
   *  not "Already replied") passes its own lookup instead. */
  outcomeCopy?: (reason: string) => string;
  /** Copy for a failure reason the caller knows; null leaves it in the plain
   *  "Failed · n" count, so a raw Go error is never rendered. */
  failureCopy?: (reason: string) => string | null;
}

interface ReasonCount {
  reason: string;
  count: number;
}

/** A heading naming what the batch is doing — only for the kinds this bar is
 *  reused for beyond the original send flow (Task 23/26); a kind with no
 *  entry here (e.g. `send_initial`) renders no extra heading. */
const OPERATION_KIND_LABELS: Record<string, string> = {
  remove_entries: 'Removing creators',
  approve_drafts: 'Approving drafts',
  erasure: 'Erasing',
  find_contacts: 'Finding contacts',
  send_to_brand: 'Moving to the brand dashboard',
  resend_messages: 'Retrying sends',
};

const APPLIED_VERBS: Record<string, string> = { send_to_brand: 'Moved' };

function groupBy(operation: OutreachOperation, outcome: 'skipped' | 'failed'): ReasonCount[] {
  const counts = new Map<string, number>();
  for (const item of operation.outcomes ?? []) {
    if (item.outcome !== outcome) continue;
    const reason = item.reason || 'unknown';
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([reason, count]) => ({ reason, count }))
    .sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

export function OperationProgress({ campaignId, operationId, onDone, onError, outcomeCopy = skipLabel, failureCopy }: OperationProgressProps) {
  const { data: operation, isError } = useOutreachOperation(campaignId, operationId);

  const doneRef = useRef(false);
  useEffect(() => {
    if (doneRef.current) return;
    if (isError) {
      doneRef.current = true;
      onError?.();
      return;
    }
    if (!operation || operation.status === 'running') return;
    doneRef.current = true;
    onDone?.(operation);
  }, [operation, isError, onDone, onError]);

  if (isError) {
    return <p className="text-body-sm-regular text-rose-500">Couldn&apos;t read this batch&apos;s progress.</p>;
  }
  if (!operation) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-2 w-full rounded-full" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  const failed = failedTotal(operation);
  const settled = operation.appliedCount + operation.skippedCount + failed;
  const total = Math.max(operation.total, settled);
  const pct = total > 0 ? Math.round((settled / total) * 100) : 0;
  const skips = groupBy(operation, 'skipped');
  const groupedSkips = skips.reduce((sum, s) => sum + s.count, 0);
  const ungroupedSkips = operation.skippedCount - groupedSkips;
  const kindLabel = OPERATION_KIND_LABELS[operation.kind];
  const wordedFailures = new Map<string, number>();
  for (const f of failureCopy ? groupBy(operation, 'failed') : []) {
    const copy = failureCopy?.(f.reason);
    if (copy) wordedFailures.set(copy, (wordedFailures.get(copy) ?? 0) + f.count);
  }
  const plainFailures = failed - [...wordedFailures.values()].reduce((sum, n) => sum + n, 0);

  return (
    <div className="flex flex-col gap-3">
      {kindLabel && <p className="text-body-sm-medium text-foreground">{kindLabel}</p>}
      <div
        role="progressbar"
        aria-label="Send progress"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={settled}
        className="relative h-2 w-full overflow-hidden rounded-full bg-primary/20"
      >
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${pct}%` }} />
      </div>

      <p className="text-body-sm-medium text-foreground">
        {`${APPLIED_VERBS[operation.kind] ?? 'Queued'} ${operation.appliedCount} of ${total}`}
        {operation.status === 'running' ? ' — still going…' : ''}
      </p>

      {skips.length > 0 || ungroupedSkips > 0 || failed > 0 ? (
        <ul className="flex flex-col gap-1">
          {skips.map((s) => (
            <li key={s.reason} className="text-caption-regular text-muted-foreground">
              {`${outcomeCopy(s.reason)} · ${s.count}`}
            </li>
          ))}
          {ungroupedSkips > 0 ? (
            <li className="text-caption-regular text-muted-foreground">{`Skipped · ${ungroupedSkips}`}</li>
          ) : null}
          {[...wordedFailures].map(([copy, count]) => (
            <li key={copy} className="text-caption-regular text-rose-600">{`${copy} · ${count}`}</li>
          ))}
          {plainFailures > 0 ? (
            <li className="text-caption-regular text-rose-600">{`Failed · ${plainFailures}`}</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/** "Queued 47 · skipped 3 (No email on file 2, …)" — the batch summary toast. */
export function operationSummary(operation: OutreachOperation): string {
  const parts = [`Queued ${operation.appliedCount}`];
  if (operation.skippedCount > 0) {
    const detail = groupBy(operation, 'skipped')
      .map((s) => `${skipLabel(s.reason)} ${s.count}`)
      .join(', ');
    parts.push(`skipped ${operation.skippedCount}${detail ? ` (${detail})` : ''}`);
  }
  if (operation.failedCount > 0) parts.push(`failed ${operation.failedCount}`);
  return parts.join(' · ');
}
